import type { EmployeeInfo } from "../interfaces/EmployeeInfoInterface";
import type { EmployeeEnrollment, EnrollmentState } from "../interfaces/SelfieEnrollment";

/**
 * Enrollment-photo coverage helpers for the Employees roster (TAS-220).
 * Pure functions only — no React, no API — so the CSV/TSV builders are unit-testable.
 */

/** A photo is "on file" when the roster row carries a blob URL. The API always emits the key
 *  (`?? null`), so `undefined` only means an older API build — treated as no photo. */
export function hasPhoto(e: Pick<EmployeeInfo, "enrollmentPhotoUrl">): boolean {
    return !!e.enrollmentPhotoUrl;
}

/** Enrollment state for a roster row (TAS-568). `enrollment.state` when the API sends it;
 *  an older API build falls back to photo presence (on file = enrolled). */
export function enrollmentState(e: Pick<EmployeeInfo, "enrollmentPhotoUrl" | "enrollment">): EnrollmentState {
    return e.enrollment?.state ?? (hasPhoto(e) ? "enrolled" : "none");
}

/** Photo column sort, ascending: Failed → Needed → Verifying → On file. */
export const PHOTO_SORT_RANK: Record<EnrollmentState, number> = { failed: 0, none: 1, verifying: 2, enrolled: 3 };

/** Photo filter / result-chip labels. */
export const PHOTO_STATE_LABEL: Record<EnrollmentState, string> = {
    enrolled: "On file",
    verifying: "Verifying",
    failed: "Failed",
    none: "Needed",
};

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** ISO datetime (UTC from the API) → "Mar 2, 2026" in the viewer's local time. */
export function formatPhotoDate(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** One row of the "Employees Needing Photos" list — already resolved to display strings. */
export interface MissingPhotoRow {
    employeeID: number;
    altEmployeeID: string;
    firstName: string;
    lastName: string;
    name: string;
    role: string;
    jobTitle: string;
    supervisor: string;
    /** "101 — Anaheim, CA" (altLocationID — name), used for grouping and export. */
    locationLabel: string;
    phone: string;
    /** The gap: no photo at all, or a photo Azure rejected. Verifying is never on this list. */
    state: "none" | "failed";
    /** Present for `failed` — drives the reason chip. */
    enrollment?: EmployeeEnrollment;
    /** "Needs a photo" or "Enrollment failed: More than one face" (Copy / CSV). */
    status: string;
}

/** Failed rows first (they need a fix, not an upload), then by name — groupByLocation keeps this order. */
export function orderNeedingPhotos(rows: MissingPhotoRow[]): MissingPhotoRow[] {
    const key = (r: MissingPhotoRow) => `${r.lastName} ${r.firstName}`.toLowerCase();
    return rows
        .slice()
        .sort((a, b) => (a.state === b.state ? key(a).localeCompare(key(b)) : a.state === "failed" ? -1 : 1));
}

/** Tab-separated `ID⇥Name⇥Status⇥Supervisor⇥Location`, one row per line — pastes into Excel/Sheets. */
export function toTsv(rows: MissingPhotoRow[]): string {
    return rows
        .map((r) => [r.altEmployeeID, r.name, r.status, r.supervisor, r.locationLabel].join("\t"))
        .join("\n");
}

export const CSV_HEADER = ["Employee ID", "Name", "Role", "Job Title", "Supervisor", "Location", "Cell Phone", "Status"];

function csvCell(value: string): string {
    return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

/** RFC-4180 style CSV: every value double-quoted with `""` escaping, CRLF line endings. */
export function toCsv(rows: MissingPhotoRow[]): string {
    const lines = [CSV_HEADER].concat(
        rows.map((r) => [r.altEmployeeID, r.name, r.role, r.jobTitle, r.supervisor, r.locationLabel, r.phone, r.status])
    );
    return lines.map((cells) => cells.map(csvCell).join(",")).join("\r\n");
}

/** Group rows by location label, groups sorted by label, row order preserved within a group. */
export function groupByLocation(rows: MissingPhotoRow[]): { label: string; rows: MissingPhotoRow[] }[] {
    const byLabel = new Map<string, MissingPhotoRow[]>();
    rows.forEach((r) => {
        const list = byLabel.get(r.locationLabel);
        if (list) list.push(r);
        else byLabel.set(r.locationLabel, [r]);
    });
    return Array.from(byLabel.entries())
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
        .map(([label, list]) => ({ label, rows: list }));
}

/** Browser download of a text payload — the same anchor dance ProjectsPage uses for xlsx blobs. */
export function downloadTextFile(fileName: string, text: string, mime = "text/csv"): void {
    const url = URL.createObjectURL(new Blob([text], { type: mime }));
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
